/**
 * Auto-computed column statistics.
 *
 * Provides type-detected statistics for datasets, enabling LLMs to understand
 * data distributions without manual aggregation requests.
 */

import { toDateValue } from '#core/lib/expr/ops/transform/date-utils.ts';
import { inferColumnTypeFromCells } from '#core/lib/expr/schema/refine.ts';
import type { ColumnType } from '#core/lib/expr/schema/types.ts';
import type { Cell, Columns } from '#core/lib/expr/value.ts';
import { numericScalarOf } from '#core/lib/expr/value.ts';
import { computeNumericStats, type NumericColumnStats } from '#core/lib/stats/numeric-stats.ts';

/**
 * Stats for string columns. Never computed — a string column's facts (null
 * count, distinct count, distribution) live on the column summary itself —
 * but persisted thread summaries carry this variant, so the render-side
 * narrowing keeps a branch for it.
 */
export interface StringColumnStats {
  type: 'string';
  count: number;
  nullCount: number;
  distinctCount: number;
}

/** Stats for date columns. `min`/`max` are epoch-ms (the storage format); `null` when no temporal values are present. */
export interface DateColumnStats {
  type: 'date';
  count: number;
  nullCount: number;
  min: number | null;
  max: number | null;
}

/** Stats for columns with mixed types */
export interface MixedColumnStats {
  type: 'mixed';
  count: number;
  nullCount: number;
}

/** Stats for struct / reference columns, with per-member non-null coverage. */
export interface StructColumnStats {
  type: 'struct';
  count: number;
  nullCount: number;
  fields: Record<string, { nonNull: number }>;
}

export type ColumnStats =
  | NumericColumnStats
  | StringColumnStats
  | DateColumnStats
  | MixedColumnStats
  | StructColumnStats;

/**
 * Local vocabulary for the stats-output shape. `none` marks scalar-leaf kinds
 * whose facts (null count, distinct count, distribution) live entirely on the
 * column summary — stats for them would only duplicate those facts.
 */
type AutoStatsColumnType = 'numeric' | 'date' | 'mixed' | 'struct' | 'none';
/** Lookup mapping the canonical engine {@link ColumnType} kind to the auto-stats column kind. */
const AUTO_STATS_TYPE: Partial<Record<ColumnType['kind'], AutoStatsColumnType>> = {
  number: 'numeric',
  integer: 'numeric',
  timestamp: 'date',
  date: 'date',
  string: 'none',
  enum: 'none',
  boolean: 'none',
  struct: 'struct',
};

/**
 * Maps a column's inferred canonical {@link ColumnType} kind to the auto-stats
 * column kind. Auto-stats uses its own local vocabulary ('date', 'mixed') for
 * its stats-output shape, so this translator bridges the two spaces; any kind
 * with no stats variant (including the inference fallback `unknown`) collapses
 * to 'mixed'.
 */
function mapToAutoStatsType(cells: readonly Cell[]): AutoStatsColumnType {
  return AUTO_STATS_TYPE[inferColumnTypeFromCells(cells).kind] ?? 'mixed';
}

/**
 * Compute statistics for a date column. Accepts `Date` cells, ISO strings, and
 * epoch-ms numbers — all coerced through {@link toDateValue} — and reports the
 * span as epoch-ms min/max so the renderer formats it in the display timezone.
 */
function computeDateStats(values: readonly unknown[]): DateColumnStats {
  const nullCount = values.filter((v) => v === null || v === undefined).length;
  const epochs = values
    .map((v) => toDateValue(v))
    .filter((d): d is Date => d !== null)
    .map((d) => d.getTime());

  if (epochs.length === 0) {
    return { type: 'date', count: 0, nullCount, min: null, max: null };
  }

  return {
    type: 'date',
    count: epochs.length,
    nullCount,
    min: Math.min(...epochs),
    max: Math.max(...epochs),
  };
}

/**
 * Compute statistics for a mixed column.
 */
function computeMixedStats(values: readonly unknown[]): MixedColumnStats {
  const nullCount = values.filter((v) => v === null || v === undefined).length;

  return {
    type: 'mixed',
    count: values.length - nullCount,
    nullCount,
  };
}

/**
 * Compute statistics for a struct / reference column. Member coverage is taken
 * over the non-null object cells, keyed by the union of member names seen across
 * them (so a partially-populated member still surfaces its sparse coverage).
 */
function computeStructStats(values: readonly unknown[]): StructColumnStats {
  const objects = values.filter((v): v is Record<string, unknown> => typeof v === 'object' && v !== null);
  const nullCount = values.filter((v) => v === null || v === undefined).length;
  const memberNames = [...new Set(objects.flatMap((o) => Object.keys(o)))];

  const fields = Object.fromEntries(
    memberNames.map((name) => [
      name,
      { nonNull: objects.filter((o) => o[name] !== null && o[name] !== undefined).length },
    ]),
  );

  return {
    type: 'struct',
    count: objects.length,
    nullCount,
    fields,
  };
}

/**
 * The stats computer for one auto-stats kind — a narrowing chain
 * terminated by `satisfies never`, so adding a kind is a compile error
 * here until a branch is supplied. `none` kinds produce no stats.
 */
function statsFor(kind: AutoStatsColumnType, values: readonly Cell[]): ColumnStats | undefined {
  if (kind === 'none') {
    return undefined;
  }
  if (kind === 'numeric') {
    // Unbox unit-bearing cells to their bare numbers first — the generic
    // stats kernel only sees plain numerics, so boxed cells summarize as
    // numbers.
    return computeNumericStats(values.map((v) => numericScalarOf(v) ?? v));
  }
  if (kind === 'date') {
    return computeDateStats(values);
  }
  if (kind === 'mixed') {
    return computeMixedStats(values);
  }
  if (kind === 'struct') {
    return computeStructStats(values);
  }
  return kind satisfies never;
}

/**
 * Compute per-column statistics for a column-major dataset.
 *
 * Infers each column's canonical type from its cells and computes
 * type-appropriate statistics:
 * - Numeric: min, max, mean
 * - Date: min, max as epoch-ms
 * - Struct: per-member non-null coverage
 * - Mixed: count only
 *
 * Scalar-leaf columns with no stats variant (string / enum / boolean) produce
 * no entry — their facts live on the column summary itself.
 */
export function computeAutoStats(columns: Columns): Record<string, ColumnStats> {
  return Object.fromEntries(
    Object.entries(columns).flatMap(([name, cells]) => {
      // No cells → no type to infer and nothing to profile; an empty
      // dataset's columns carry bare facts only.
      if (cells.length === 0) {
        return [];
      }
      const stats = statsFor(mapToAutoStatsType(cells), cells);
      return stats === undefined ? [] : [[name, stats] as const];
    }),
  );
}
